import { createSlice } from "@reduxjs/toolkit";
import { REDUX_API_STATUS } from "../../utils/constants.ts";
import { AuthState } from "../../utils/types.ts";

const { INITIALIZING, IDLE, LOADING, SUCCESS, FAILED } = REDUX_API_STATUS;

// Auth status state machine:
//   INITIALIZING -> app just booted; session restore / OAuth callback pending
//   IDLE         -> known signed-out; router shows Login
//   LOADING      -> redirecting to the IdP, or fetching the user profile
//   SUCCESS      -> authenticated; router shows the app
//   FAILED       -> sign-in or profile fetch failed; Login shows authError
// This slice is intentionally NOT persisted: auth status must be re-derived
// from the real Cognito session on every page load (see App.tsx bootstrap).
const INITIAL_STATE: AuthState = {
  authAPIStatus: INITIALIZING,
  authError: null,
  user: {
    userId: null,
    email: null,
    firstName: null,
    lastName: null,
    isActive: false,
    isAdmin: false,
    isStaff: false,
    permissions: [],
    userType: null,
    groups: [],
    clients: [],
    owners: [],
    applications: [],
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState: INITIAL_STATE,
  reducers: {
    setAuthLoading: (state) => {
      state.authAPIStatus = LOADING;
      state.authError = null;
    },
    setAuthIdle: (state) => {
      state.authAPIStatus = IDLE;
      state.authError = null;
    },
    setAuthFailed: (state, action) => {
      state.authAPIStatus = FAILED;
      state.authError =
        typeof action.payload === "string" && action.payload
          ? action.payload
          : "Something went wrong while signing you in. Please try again.";
    },
    setAuthData: (state, action) => {
      state.authAPIStatus = SUCCESS;
      state.authError = null;
      state.user = action.payload;
    },
    logout: (state) => {
      state.authAPIStatus = IDLE;
      state.authError = null;
      state.user = INITIAL_STATE.user;
    },
  },
});

export const {
  setAuthLoading,
  setAuthData,
  logout,
  setAuthIdle,
  setAuthFailed,
} = authSlice.actions;

export default authSlice.reducer;
